"use client";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "./context/AuthContext";
import { useToast } from "../hooks/use-toast";
import { motion } from "motion/react";
import {
  ChevronRight,
  Gift,
  Users,
  Briefcase,
  Heart,
  HelpCircle,
  LogOut,
  Info,
  BookOpen,
} from "lucide-react";

export function NavbarDemo() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    {
      name: "Services",
      link: "/services",
    },
    {
      name: "About Us",
      link: "/about",
    },
    {
      name: "Blogs",
      link: "/blogs",
    },
    {
      name: "Gift Cards",
      link: "/gift-cards",
    },
    {
      name: "Contact",
      link: "/contact",
    },
  ];

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out.",
    });
    navigate("/");
  };

  return (
    <div className="relative w-full">
      <Navbar>
        {/* Desktop Navigation */}
        <NavBody>
          <NavbarLogo />
          <NavItems items={navItems} />
          <div className="flex items-center gap-2 ml-4">
            {isAuthenticated ? (
              <>
                <NavbarButton
                  variant="secondary"
                  href="/dashboard"
                  className="whitespace-nowrap">
                  Dashboard
                </NavbarButton>
                <NavbarButton
                  variant="secondary"
                  href="/rewards"
                  className="whitespace-nowrap">
                  Rewards
                </NavbarButton>
                <NavbarButton
                  as="button"
                  variant="primary"
                  onClick={handleLogout}
                  className="whitespace-nowrap">
                  Logout
                </NavbarButton>
              </>
            ) : (
              <>
                <NavbarButton
                  variant="secondary"
                  href="/auth"
                  className="whitespace-nowrap">
                  Login
                </NavbarButton>
                <NavbarButton
                  variant="primary"
                  href="/book"
                  className="whitespace-nowrap">
                  Book a Therapy
                </NavbarButton>
              </>
            )}
          </div>
        </NavBody>

        {/* Mobile Navigation */}
        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}>
            {/* Enhanced Mobile Menu with Icons */}
            <div className="flex flex-col gap-2 p-3">
              {/* Dashboard - Only show if authenticated */}
              {isAuthenticated && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-between py-4 px-5 text-base font-semibold text-gray-800 bg-gradient-to-r from-white to-gray-50 hover:from-[#98C454]/10 hover:to-[#87b043]/10 rounded-2xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                    <span className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-[#98C454]" />
                      Dashboard
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </Link>
                </motion.div>
              )}

              {/* Book a Therapy */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: isAuthenticated ? 0.2 : 0.1, duration: 0.3 }}
              >
                <Link
                  to="/book"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-5 px-5 text-base font-bold bg-gradient-to-r from-[#98C454] to-[#87b043] text-white rounded-2xl shadow-[0_4px_16px_rgba(152,196,84,0.3)] hover:shadow-[0_6px_24px_rgba(152,196,84,0.4)] transition-all duration-300 hover:scale-[1.02] mb-2 relative overflow-hidden">
                  {/* Shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
                  <span className="flex items-center gap-3 relative z-10">
                    <Heart className="h-6 w-6" />
                    Book a Therapy
                  </span>
                  <ChevronRight className="h-6 w-6 relative z-10" />
                </Link>
              </motion.div>

              {/* Login/Sign up - Only show if not authenticated */}
              {!isAuthenticated && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <Link
                    to="/auth"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                    <span className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-[#98C454]" />
                      Login/Sign up
                    </span>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </Link>
                </motion.div>
              )}

              {/* Services */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <Link
                  to="/services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                  <span className="flex items-center gap-3">
                    <Heart className="h-5 w-5 text-[#98C454]" />
                    Services
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              </motion.div>

              {/* About Us */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
              >
                <Link
                  to="/about"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                  <span className="flex items-center gap-3">
                    <Info className="h-5 w-5 text-[#98C454]" />
                    About Us
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              </motion.div>

              {/* Blogs */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <Link
                  to="/blogs"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                  <span className="flex items-center gap-3">
                    <BookOpen className="h-5 w-5 text-[#98C454]" />
                    Blogs
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              </motion.div>

              {/* Gift Cards */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              >
                <Link
                  to="/gift-cards"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                  <span className="flex items-center gap-3">
                    <Gift className="h-5 w-5 text-[#98C454]" />
                    Gift Cards
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              </motion.div>

              {/* Contact */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7, duration: 0.3 }}
              >
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-gray-800 bg-white hover:bg-[#98C454]/5 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md border border-gray-100 hover:border-[#98C454]/30">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="h-5 w-5 text-[#98C454]" />
                    Contact
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              </motion.div>

              {/* Separator */}
              <motion.div
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ delay: 0.8, duration: 0.3 }}
                className="my-3 border-t border-gray-200"
              ></motion.div>

              {/* Log out - Only show if authenticated */}
              {isAuthenticated && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-between py-3.5 px-5 text-base font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md w-full text-left border border-red-100 hover:border-red-200">
                    <span className="flex items-center gap-3">
                      <LogOut className="h-5 w-5" />
                      Log out
                    </span>
                    <ChevronRight className="h-5 w-5 text-red-400" />
                  </button>
                </motion.div>
              )}
            </div>
          </MobileNavMenu>
        </MobileNav>
      </Navbar>
    </div>
  );
}
